import React, {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { useContract, useRole } from "../ContractContext";
import { makeCancelable, Cancelable } from "../../utils";
import { Button } from "@teamscheme/button";
import { TextLink } from "@teamscheme/text-link";
import { useInvitationStatus } from "../invitation/InvitationStatus";
import { useRoleName } from "../ContractContext";
import ROLES from "../roles";
import { captureException } from "@sentry/core";
import { useLoadOncePerLocation } from "../../shared/useLoadOncePerLocation";
import { useContractClient } from "../../api/ContractClient";
import ErrorBox from "../../shared/elements/ErrorBox";
import InfoBox from "../../shared/elements/InfoBox";
import { ApiError } from "../../api/ApiClient";
import { useLink } from "../../useLink";
import { Role } from "../model/ContractViewModel";
import { createLocation } from "history";

export const ContractValidationView = ({
  validation,
  ...props
}: {
  validation: IContractValidationContext;
} & Omit<ContractValidationResultProps, "result">) => {
  const { loading, error, result, retry } = validation;

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <p>
        Kunne ikke validere kontrakten.{" "}
        <Button variant="secondary" onClick={retry}>
          Prøv igjen!
        </Button>
      </p>
    );
  }

  return result ? (
    <ContractValidationResultView {...props} result={result} />
  ) : null;
};

export interface ContractValidationResultProps {
  result: ContractValidationResult;
  onClickBack: () => void;
}

export function ContractValidationResultView({
  result: { isValid, hasBuyerResponsibleError, hasSellerResponsibleError },
  onClickBack,
}: ContractValidationResultProps) {
  const role = useRole();
  const { shouldInvite, hasAccepted, hasInvited, invitationTypeString } =
    useInvitationStatus();

  const { other } = useRoleName();

  const hasOwnError =
    (hasBuyerResponsibleError && role === ROLES.BUYER) ||
    (hasSellerResponsibleError && role === ROLES.SELLER);

  const hasOtherError =
    (hasBuyerResponsibleError && role === ROLES.SELLER) ||
    (hasSellerResponsibleError && role === ROLES.BUYER);

  const contractLink = useLink((urls) => urls.contract);
  const inviteLink = useLink((urls) => urls.invite);

  return (
    <>
      {isValid ? <p>Alt ser bra ut!</p> : null}
      {hasOwnError ? (
        <ErrorBox>
          Du har ikke fullført din del av kontrakten.{" "}
          <TextLink linkTo={contractLink} onClick={onClickBack}>
            Gå tilbake og fyll ut resten.
          </TextLink>
        </ErrorBox>
      ) : null}
      {hasOtherError ? (
        <InfoBox>
          <span>
            {shouldInvite && hasInvited ? (
              <span>
                Invitasjon er sendt til {other} via {invitationTypeString}.{" "}
              </span>
            ) : null}
            <span>
              Vennligst vent til {other} har fylt ut sin del av kontrakten slik
              at dere kan signere.
            </span>
          </span>
          {shouldInvite && !hasAccepted ? (
            <span>
              <span> Har ikke {other} mottatt invitasjon?</span>
              <br />
              <TextLink linkTo={createLocation(inviteLink, { resend: true })}>
                Inviter {other} på nytt
              </TextLink>
            </span>
          ) : null}
        </InfoBox>
      ) : null}
    </>
  );
}

function useContractValidationClient(): IContractValidationContext {
  const contract = useContract();
  const client = useContractClient();
  const [loading, setValidationLoading] = useState(true);
  const [error, setValidationError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<ContractValidationResult | null>(null);
  const [retries, setRetries] = useState(0);

  function retry() {
    setRetries((retries) => retries + 1);
  }

  const validate = useCallback(async function validate(
    cancelable: Cancelable<ContractValidationResult>
  ) {
    setValidationLoading(true);

    try {
      await cancelable.promise;
      setResult(null);
      setValidationLoading(false);
      setValidationError(null);
    } catch (error) {
      if (!error.isCanceled) {
        if (error.status === 400) {
          setResult(error.result);
        } else {
          setValidationError(error);
          captureException(error);
          console.log("validationError", error);
        }
        setValidationLoading(false);
      }
    }
  },
  []);

  const { loadedLocation, load } = useLoadOncePerLocation();

  useEffect(() => {
    if (!loadedLocation) return;

    const cancelablePromise = makeCancelable(
      client.post<ContractValidationResult>(`contract/${contract.id}/validate`)
    );

    validate(cancelablePromise);

    return () => cancelablePromise.cancel();
  }, [contract, retries, validate, client, loadedLocation]);

  return {
    loading,
    error,
    result,
    retry,
    load,
  };
}

export interface ContractValidationError {
  role: Role;
  section: string;
  field: string;
  isRequired: boolean;
  patternError?: string;
}

export interface ContractValidationResult {
  isValid: boolean;
  errors: ContractValidationError[];
  hasBuyerInviterError: boolean;
  hasSellerInviterError: boolean;
  hasSellerResponsibleError: boolean;
  hasBuyerResponsibleError: boolean;
  hasBuyerError: boolean;
  hasSellerError: boolean;
  hasConditionError: boolean;
  hasVehicleDataError: boolean;
  hasSettlementError: boolean;
}

export interface IContractValidationContext {
  load(): void;
  retry(): void;
  result: ContractValidationResult | null;
  error: ApiError | null;
  loading: boolean;
}

const ContractValidationContext =
  createContext<IContractValidationContext | null>(null);

export function ContractValidationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const context = useContractValidationClient();

  return (
    <ContractValidationContext.Provider value={context}>
      {children}
    </ContractValidationContext.Provider>
  );
}

export function useContractValidation() {
  const context = useContext(ContractValidationContext);
  const [loading, setLoading] = useState(true);

  if (!context) throw new Error("No ContractValidationContext found!");

  const { load } = context;

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setLoading(context.loading);
  }, [context.loading]);

  return { ...context, loading: loading || context.loading };
}
